import { useJsApiLoader } from '@react-google-maps/api';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import camelCase from 'lodash.camelcase';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CollectionPoint } from '../../../types/collectionPoints';
import callApi from '../../../utils/callApi';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import { geocode } from '../../../utils/geocode';
import { reportErrorToSentry } from '../../../utils/initializeSentry';
import { joinZipCodeCity } from '../../../utils/streetHouseNumber';
import { pushTrackingEvent } from '../../../utils/tracking';
import { showDefaultError, showNotification } from '../Notifications';
import Filters from './Filters';
import { getEmptySearch, getInitialSearchFromAddress, getInitialSearchFromCurrentPosition } from './initialSearch';
import LocationDetails from './LocationDetails';
import LocationList from './LocationList';
import Map from './Map';
import Search, { CollectionPointSearchParams } from './Search';
import './style.scss';
import View, { ViewOption } from './View';

const getCollectionPointI18nKey = (location: CollectionPoint) =>
  camelCase(
    location.deliveryOption.collectionPointType === 'post_office'
      ? `${location.deliveryOption.carrierGroupKey}_postoffice`
      : location.deliveryOption.collectionPointType === 'parcel_shop'
      ? `${location.deliveryOption.carrierGroupKey}_parcelshop`
      : location.deliveryOption
          .collectionPointType!.replace('reba', location.deliveryOption.carrierGroupKey)
          .replace('UB_', '')
          .replace('parcel_shop', 'parcelshop')
          .replace('servicepoint', 'parcelshop')
          .replace('post_office', 'postoffice')
          .replace('parcelbox', 'packstation')
  );

const BackButton = ({ view, onClick }: { view: 'map' | 'list'; onClick: () => void }) => (
  <div className="collection-points__back-button" onClick={onClick}>
    <img src={getAssetUrl('collectionPoints/chevronLeft.svg')} alt="left" />
    <p>
      <FormattedMessage
        id="shipping.collectionPoint.back"
        values={{ view: <FormattedMessage id={`shipping.collectionPoint.views.${view}`} /> }}
      />
    </p>
  </div>
);

const CollectionPoints = () => {
  const [locations, setLocations] = useState<CollectionPoint[]>();
  const { checkoutState } = useCheckoutState();
  const [isSearching, setIsSearching] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const { isLoaded: isGoogleMapsSdkLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    region: config.country,
    language: config.locale.replace('_', '-'),
  });
  const [storedSearch, setStoredSearch] = useState<CollectionPointSearchParams>(getEmptySearch());
  const [view, setView] = useState<ViewOption>('map');
  const isMapView = view === 'map';
  const [filter, setFilter] = useState('');
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext<any>();
  const shouldSelectRef = useRef(false);
  const selectedLocation = values.collectionPoint.id;

  useEffect(() => {
    if (!isGoogleMapsSdkLoaded) {
      return;
    }
    (async () => {
      const existingShippingAddress = checkoutState.orderState.addresses?.shipping;
      const initialSearch = existingShippingAddress
        ? getInitialSearchFromAddress(existingShippingAddress)
        : await getInitialSearchFromCurrentPosition();
      if (initialSearch) {
        search(initialSearch);
        if (existingShippingAddress?.collectionPoint) {
          shouldSelectRef.current = true;
        }
      }
    })();
  }, [isGoogleMapsSdkLoaded]);

  useEffect(() => {
    if (!locations || !shouldSelectRef.current) {
      return;
    }
    const collectionPointId = checkoutState.orderState.addresses?.shipping?.collectionPoint?.id;
    if (collectionPointId) {
      selectLocation(collectionPointId);
    }
    shouldSelectRef.current = false;
  }, [locations]);

  const locationsWithI18nKey = useMemo(
    () =>
      locations?.map(location => ({
        ...location,
        i18nKey: getCollectionPointI18nKey(location),
      })),
    [locations]
  );

  const availableFilters =
    locationsWithI18nKey &&
    [...new Set(locationsWithI18nKey.map(location => location.deliveryOption.collectionPointType))].map(
      collectionPointType => ({
        i18nKey: locationsWithI18nKey.find(
          location => location.deliveryOption.collectionPointType === collectionPointType
        )!.i18nKey,
        collectionPointType: collectionPointType!,
      })
    );

  const filteredLocations = useMemo(
    () => locationsWithI18nKey?.filter(location => location.deliveryOption.collectionPointType === filter),
    [locationsWithI18nKey, filter]
  );
  const locationsToRender = filter ? filteredLocations : locationsWithI18nKey;

  const statusCodeI18nKeys = {
    404: 'shipping.notifications.noCollectionPointsFound',
    422: 'shipping.notifications.validationError',
  };

  const search = async ({ streetHouseNumber, zipCode, city, maximumDistance }: CollectionPointSearchParams) => {
    setIsSearching(true);
    setStoredSearch({ streetHouseNumber, zipCode, city, maximumDistance });
    const cityZipCode = joinZipCodeCity({ zipCode, city, country: { iso2Code: config.country } });
    try {
      const coordinates = await geocode([streetHouseNumber, cityZipCode].join(', '));
      const collectionPointSearchResponse = await callApi({
        endpoint: '/collection-points',
        urlParams: {
          streetHouseNumber,
          zipCode,
          city,
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString(),
          maximumDistance: maximumDistance.toString(),
          maximumResults: '100',
        },
      });
      setLocations(collectionPointSearchResponse.items);
      setIsSearching(false);
      setIsEditing(false);
    } catch (e: any) {
      setIsSearching(false);
      pushTrackingEvent({
        event: 'error',
        action: 'collection_points',
        label: `${config.country}|${e.name}|${e.message}`,
        value: null,
      });
      const errorI18nKey = statusCodeI18nKeys[e.statusCode];
      if (errorI18nKey) {
        showNotification({
          level: 'warning',
          text: formatMessage({ id: errorI18nKey }),
          autoClose: true,
        });
        return;
      }
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  const selectLocation = (id: string) => {
    const { carrierKey, carrierGroupKey, shippingOptionKey, collectionPointType } = locations!.find(
      location => location.specification.collectionPointId === id
    )!.deliveryOption;
    setFieldValue('collectionPoint.id', id);
    setFieldValue('collectionPoint.type', { carrierKey, carrierGroupKey, shippingOptionKey, collectionPointType });
  };

  const selectedLocationDetails = locationsWithI18nKey?.find(
    location => selectedLocation === location.specification.collectionPointId
  );

  const onBackButtonClick = () => {
    setFieldValue('collectionPoint.id', '');
    setFieldValue('collectionPoint.type', {
      carrierGroupKey: '',
      carrierKey: '',
      shippingOptionKey: '',
      collectionPointType: '',
    });
  };

  return (
    <div
      className={classNames('collection-points', {
        'collection-points--list-view': !isMapView,
        'collection-points--list-view-with-location-details': !isMapView && !!selectedLocationDetails,
        'collection-points--with-location-details': !!selectedLocationDetails,
      })}
    >
      {!(selectedLocation && selectedLocationDetails) ? (
        <>
          <div className={classNames({ 'collection-points__search-wrapper': isMapView })}>
            <Search
              onSearch={search}
              onSearchEdit={() => setIsEditing(true)}
              isEditing={isEditing}
              storedSearch={storedSearch}
              isSearching={isSearching}
            />
            {!!availableFilters?.length && (
              <Filters
                availableFilters={availableFilters}
                activeFilter={filter}
                onClick={newFilter => setFilter(newFilter)}
              />
            )}
          </div>
          <Map
            onMarkerClick={id => selectLocation(id)}
            locations={locationsToRender}
            isGoogleMapsSdkLoaded={isGoogleMapsSdkLoaded}
            view={view}
          />
          <LocationList locations={locationsToRender} onItemClick={id => selectLocation(id)} />
          <View value={view} onChange={setView} />
        </>
      ) : (
        <>
          <BackButton view={view} onClick={onBackButtonClick} />
          <LocationDetails selectedLocationDetails={selectedLocationDetails} />
          <Map selectedLocationDetails={selectedLocationDetails} isGoogleMapsSdkLoaded={isGoogleMapsSdkLoaded} />
        </>
      )}
    </div>
  );
};

export default CollectionPoints;
